import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BanknotesIcon,
  BuildingLibraryIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import CurrencyInput from "@/components/CurrencyInput/CurrencyInput.component";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import Selector from "@/components/Selector";
import { Select, SelectOption } from "@/components/Select";
import { default as ReactSelect } from "react-select";
import { useCatalogStore } from "@/stores/catalogs/country.store";
import { useQuoterStore } from "@/stores/quoter/quoter.store";
import LoadingIcon from "@/assets/Icons/loading.svg?react";
import {
  DeliveryMethodCode,
  DeliveryMethods,
  PaymentMethod,
  PaymentMethodCode,
  PaymentMethodConfig,
  QuoteChannel,
  QuoterFormValues,
} from "@/interfaces/quoter.interface";
import { Destination, ICountry } from "@/interfaces/catalogs.interface";
import { useDebounce } from "@/hooks/useDebounce";
import USIcon from "@/assets/Img/Flags/USIcon.png";
import ZelleIcon from "@/assets/Icons/payment-methods/zelle.png";
import VenmoIcon from "@/assets/Icons/payment-methods/venmo.svg?react";
import PayPalIcon from "@/assets/Icons/payment-methods/paypal.svg?react";
import AchIcon from "@/assets/Icons/payment-methods/ach.svg?react";
import { MultiSelector } from "@/components/MultiSelector";
import { QuoteTotals } from "@/components/QuoteTotals/QuoteTotals";
import { AllowedPaymentMethods } from "@/constants/Quoter";
import { primarySelectClassNames } from "@/constants/Select";
import { ChannelSelectCard } from "@/components/MultiSelector/ChannelSelectCard";
import { PaymentMethodOption } from "@/components/PaymentMethodOption";
import { PaymentMethodSelectCard } from "@/components/MultiSelector/PaymentMethodSelectCard";
import { Controller, useForm, WatchObserver } from "react-hook-form";
import { formatNumber } from "@/utilities/NumberUtils";

const MAX_CHARACTER_AMOUNT = 15;
const AUTH_DOMAIN = import.meta.env.VITE_AUTH_DOMAIN;

const ALLOWED_COUNTRIES = [
  "MEX",
  "GTM",
  "SLV",
  "HND",
  "IND",
  "PHL",
  "NIC",
  "CAN",
  "PAN",
  "VNM",
  "COL",
  "POL",
  "PRT",
  "ARG",
];

interface GlobalPaymentsQuoterProps {
  showLegalInfo: () => void;
}

type DestinationOption = {
  label: string;
  value: string;
  destination: Destination;
};

const paymentMethodsMap: Record<PaymentMethodCode, PaymentMethodConfig> = {
  Zelle: {
    icon: (
      <img
        src={ZelleIcon}
        className="w-9 h-9 min-w-9 object-cover object-center"
      />
    ),
    order: 1,
  },
  Venmo: {
    icon: <VenmoIcon className="w-9 h-9 min-w-9" />,
    order: 2,
  },
  PayPal: {
    icon: <PayPalIcon className="w-9 h-9 min-w-9" />,
    order: 3,
  },
  "Sendola ACH": {
    icon: <AchIcon className="w-9 h-9 min-w-9" />,
    order: 4,
  },
};

export const GlobalPaymentsQuoter = ({
  showLegalInfo,
}: GlobalPaymentsQuoterProps) => {
  const isAmountToReceive = useRef(false);
  const [t, i18n] = useTranslation("global");
  const [tErrors] = useTranslation("errors");
  const currentLanguage = i18n.language;
  const [loadingQuote, setLoadingQuote] = useState<boolean>(false);
  const [loadingCountries, setLoadingCountries] = useState<boolean>(true);
  const [loadingDestinations, setLoadingDestinations] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [selectedDestination, setSelectedDestination] =
    useState<DestinationOption>();
  const [deliveryMethod, setDeliveryMethod] =
    useState<DeliveryMethodCode>("D2B");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>();
  const [CPUOptions, setCPUOptions] = useState<DestinationOption[]>([]);
  const [D2BOptions, setD2BOptions] = useState<DestinationOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<ICountry>({
    alpha2Code: "MX",
    countryCode: "MEX",
    countryName: "Mexico",
    defaultCurrency: "MXN",
    demonym: "Mexican",
    dialCode: "52",
    displayOrder: "12",
    numericCode: "484",
    officialName: "United Mexican States",
  });

  const lastQuoteAmount = useRef<number>();

  const formMethods = useForm<QuoterFormValues>({
    mode: "onChange",
    defaultValues: {
      amount: "0",
      amountToReceive: "0",
      currency: "MXN",
      country: "MEX",
      channel: QuoteChannel.bestFx,
      deliveryMethod: "D2B",
      cpuSelected: undefined,
      d2bSelected: undefined,
    },
  });
  const { control, getValues, setValue, trigger, watch } = formMethods;

  const channel = watch("channel");
  const { countries, getCountries, getDestinations } = useCatalogStore();
  const { quotesPerChannel, getQuote, resetQuote } = useQuoterStore();

  const countryOptions = useMemo(() => {
    if (!countries) return [];

    const countryMap = countries.map((country) => {
      return {
        label: country.countryName,
        shortLabel: country.defaultCurrency,
        value: country.countryCode,
        countryCode: country.countryCode,
        customElement: (
          <img
            className="w-6 h-6 object-cover rounded-full"
            src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${country.alpha2Code}.svg`}
          />
        ),
      };
    });

    return countryMap
      ?.filter((country) => ALLOWED_COUNTRIES.includes(country.countryCode))
      ?.sort((a, b) => (a.label > b.label ? 1 : -1));
  }, [countries]);

  const paymentMethodOptions = useMemo(() => {
    if (
      !quotesPerChannel?.[channel] ||
      !quotesPerChannel?.[channel]?.deliveryMethods
    ) {
      return [];
    }

    const paymentMethods = quotesPerChannel?.[
      channel
    ]?.deliveryMethods?.[0]?.paymentMethods
      ?.filter((paymentMethod) =>
        AllowedPaymentMethods.includes(paymentMethod.paymentOrigin)
      )
      ?.sort((a, b) => {
        const orderA =
          paymentMethodsMap[a.paymentOrigin as PaymentMethodCode]?.order || 999;
        const orderB =
          paymentMethodsMap[b.paymentOrigin as PaymentMethodCode]?.order || 999;

        return orderA - orderB;
      });

    const methods = paymentMethods.map((paymentMethod) => ({
      label: paymentMethod.paymentOrigin,
      value: paymentMethod.paymentOrigin,
      tariff: paymentMethod.paymentMethodFee,
    }));

    if (!paymentMethod) {
      setPaymentMethod(paymentMethods[0]);
    } else {
      setPaymentMethod(
        paymentMethods[
          paymentMethods.findIndex(
            (method) => method.paymentOrigin === paymentMethod.paymentOrigin
          )
        ]
      );
    }
    return methods;
  }, [quotesPerChannel, channel]);

  const deliveryMethodOptions = useMemo(() => {
    return [
      {
        label: t("Quoter.Account"),
        value: DeliveryMethods.ACCOUNT_DEPOSIT,
        disabled: D2BOptions?.length === 0,
        disabledText: t("Quoter.DeliveryMethod.NotAvailable"),
        icon: <BuildingLibraryIcon className="w-5 h-5 flex-shrink-0" />,
      },
      {
        label: t("Quoter.Cash"),
        value: DeliveryMethods.CASH_PICKUP,
        disabled: CPUOptions?.length === 0,
        disabledText: t("Quoter.DeliveryMethod.NotAvailable"),
        icon: <BanknotesIcon className="w-5 h-5 flex-shrink-0" />,
      },
    ];
  }, [CPUOptions, D2BOptions, i18n.language]);

  const fetchDestinations = async (
    deliveryMethod: DeliveryMethodCode,
    countryCode: string
  ) => {
    const destinations = await getDestinations(deliveryMethod, countryCode);

    const destinationOptions = destinations.map((destination) => ({
      label: destination.destination,
      value: destination.id,
      destination,
    }));
    if (deliveryMethod === "CPU") {
      setCPUOptions(destinationOptions);
      setValue("cpuSelected", destinationOptions?.[0]?.destination?.payerQuote);
    }
    if (deliveryMethod === "D2B") {
      setD2BOptions(destinationOptions);
      setValue("d2bSelected", destinationOptions?.[0]?.destination?.payerQuote);
    }

    setSelectedDestination(destinationOptions[0]);
  };

  const handleCountryChange = (value: SelectOption) => {
    setSelectedDestination(undefined);
    const country = countries.find(
      (country) => country.countryCode === value.value
    );

    if (country) {
      setSelectedCountry(country);
      setValue("channel", QuoteChannel.bestFx);
    }
  };

  const handlePaymentMethodChange = (paymentOrigin: string) => {
    const selectedPaymentMethod = quotesPerChannel?.[
      channel
    ]?.deliveryMethods?.[0]?.paymentMethods?.find(
      (paymentMethod) => paymentMethod.paymentOrigin === paymentOrigin
    );
    setPaymentMethod(selectedPaymentMethod);
  };

  const addQueryParamsToSignIn = (values: QuoterFormValues) => {
    if (
      !quotesPerChannel?.[channel] ||
      !values.amount ||
      !Number(values.amount)
    )
      return "";

    let query = `?amount=${values.amount}&currency=${selectedCountry.defaultCurrency}&country=${selectedCountry.countryCode}`;

    if (deliveryMethod !== DeliveryMethods.ACCOUNT_DEPOSIT) {
      query += `&deliveryMethod=${deliveryMethod}`;
    }

    if (values.amountToReceive && isAmountToReceive.current) {
      query += `&amountToReceive=${values.amountToReceive || 0}`;
    }

    if (selectedDestination) {
      query += `&destinationId=${selectedDestination.destination.id}`;
    }

    if (channel) {
      query += `&channel=${channel}`;
    }

    if (paymentMethod) {
      query += `&paymentMethod=${paymentMethod.paymentOrigin}`;
    }

    return `?redirectTo=${encodeURIComponent(query)}`;
  };

  const debouncedQuoteUpdate = useDebounce(
    async (quoteValues: QuoterFormValues) => {
      if (
        ((!Number(quoteValues.amount) || Number(quoteValues.amount) < 1) &&
          !isAmountToReceive.current) ||
        ((!Number(quoteValues.amountToReceive) ||
          Number(quoteValues.amountToReceive) < 1) &&
          isAmountToReceive.current) ||
        !(quoteValues.deliveryMethod === "CPU"
          ? quoteValues.cpuSelected
          : quoteValues.d2bSelected)
      ) {
        resetQuote();
        return;
      }

      setError(false);
      setLoadingQuote(true);
      try {
        await getQuote(
          quoteValues.country!,
          quoteValues.currency!,
          !isAmountToReceive.current
            ? Number(quoteValues.amount)
            : Number(quoteValues.amountToReceive),
          quoteValues.deliveryMethod === "CPU"
            ? quoteValues.cpuSelected!
            : quoteValues.d2bSelected!,
          isAmountToReceive.current
        );
      } finally {
        setLoadingQuote(false);
      }
    },
    500
  );

  const updateQuote: WatchObserver<QuoterFormValues> = async (
    values,
    { name }
  ) => {
    if (name === "deliveryMethod") {
      if (
        values.deliveryMethod === "CPU" &&
        CPUOptions?.length &&
        CPUOptions.length === 0
      ) {
        setValue("cpuSelected", undefined);
        resetQuote();
        return;
      }
      if (
        values.deliveryMethod === "D2B" &&
        D2BOptions?.length &&
        D2BOptions.length === 0
      ) {
        setValue("d2bSelected", undefined);
        resetQuote();
        return;
      }
    }

    if (
      !isAmountToReceive.current &&
      name === "amount" &&
      (values.amount === "0" || !values.amount)
    ) {
      setValue("amountToReceive", "0");
    }

    if (
      isAmountToReceive.current &&
      name === "amountToReceive" &&
      (values.amountToReceive === "0" || !values.amountToReceive)
    ) {
      setValue("amount", "0");
    }

    if (
      (name === "amount" && isAmountToReceive.current) ||
      (name === "amountToReceive" && !isAmountToReceive.current)
    ) {
      return;
    }

    if (name === "country") {
      setValue("channel", QuoteChannel.bestFx);
      values.channel = QuoteChannel.bestFx;
    }

    await debouncedQuoteUpdate(values);
  };

  const debouncedCountryFetch = useDebounce((lang: string) => {
    setLoadingCountries(true);
    getCountries(lang).finally(() => {
      setLoadingCountries(false);
    });
  }, 500);

  useEffect(() => {
    setValue("amount", "500.00"); // Initialize quoter in default amount
  }, []);

  useEffect(() => {
    if (
      deliveryMethod === DeliveryMethods.CASH_PICKUP &&
      CPUOptions.length > 0
    ) {
      setValue("cpuSelected", CPUOptions[0].destination.payerQuote);
      setSelectedDestination(CPUOptions[0]);
    } else if (
      deliveryMethod === DeliveryMethods.ACCOUNT_DEPOSIT &&
      D2BOptions.length > 0
    ) {
      setValue("d2bSelected", D2BOptions[0].destination.payerQuote);
      setSelectedDestination(D2BOptions[0]);
    } else {
      resetQuote();
    }
  }, [deliveryMethod, CPUOptions, D2BOptions]);

  useEffect(() => {
    debouncedCountryFetch(currentLanguage);
  }, [currentLanguage]);

  useEffect(() => {
    setLoadingDestinations(true);
    setValue("cpuSelected", undefined);
    setValue("d2bSelected", undefined);
    Promise.all([
      fetchDestinations(
        DeliveryMethods.CASH_PICKUP,
        selectedCountry.countryCode
      ),
      fetchDestinations(
        DeliveryMethods.ACCOUNT_DEPOSIT,
        selectedCountry.countryCode
      ),
    ]).finally(() => {
      setLoadingDestinations(false);
    });
  }, [selectedCountry]);

  useEffect(() => {
    trigger();
  }, [i18n.language]);

  useEffect(() => {
    const subscription = watch(updateQuote);

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (!quotesPerChannel) return;
    if (isAmountToReceive.current) {
      setValue("amount", `${quotesPerChannel?.[channel]?.amount || 0}`);
      lastQuoteAmount.current =
        quotesPerChannel?.[channel]?.deliveryMethods?.[0]?.amountToReceive || 0;
    }
    if (!isAmountToReceive.current) {
      setValue(
        "amountToReceive",
        `${quotesPerChannel?.[channel]?.deliveryMethods?.[0]?.amountToReceive || 0}`
      );
      lastQuoteAmount.current = quotesPerChannel?.[channel]?.amount || 0;
    }
  }, [quotesPerChannel, channel]);

  return (
    <Card className="w-full h-full flex justify-center items-center !p-4 md:p-12 !shadow-none !border-[1px] !border-gray-300 !rounded-xl">
      <form
        onSubmit={() => {}}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
        className="w-full flex flex-col items-center gap-2 max-w-screen-sm"
      >
        <div className="w-full flex flex-col gap-1">
          <p className="text-sm text-gray-600 font-[500]">
            {t("Quoter.Amount")}
          </p>
          <div className="w-full flex justify-between gap-2 h-10">
            {loadingQuote && isAmountToReceive.current ? (
              <LoadingIcon className="w-5 h-5 text-gray-400 animate-spin" />
            ) : (
              <Controller
                name="amount"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CurrencyInput
                    id="amount"
                    name="amount"
                    className="!border-0 font-semibold !px-0 text-xl min-w-36"
                    value={value || "0"}
                    placeholder="0.00"
                    allowDecimals
                    maxLength={MAX_CHARACTER_AMOUNT}
                    onValueChange={(v) => {
                      isAmountToReceive.current = false;
                      onChange(v);
                    }}
                  />
                )}
              />
            )}
            <div className="h-full w-28 justify-start overflow-hidden flex gap-1 p-2 flex-shrink-0 items-center">
              <div className="!w-24 flex items-center justify-start gap-1">
                <img
                  className="w-6 h-6 rounded-full object-cover"
                  src={USIcon}
                  alt="Language flag"
                />
                <p className="font-semibold w-10">USD</p>
              </div>
              <div className="w-4" />
            </div>
          </div>
        </div>

        <div className="w-full h-0 border-b-[1px] border-gray-300 -my-1" />

        <div className="w-full flex flex-col gap-1 max-sm:mt-2">
          <p className="text-sm text-gray-600 font-[500]">
            {t("Quoter.Receive")}
          </p>
          <div className="w-full flex justify-between gap-2 h-10">
            {loadingQuote && !isAmountToReceive.current ? (
              <LoadingIcon className="w-5 h-5 text-gray-400 animate-spin" />
            ) : (
              <Controller
                name="amountToReceive"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <CurrencyInput
                    id="amountToReceive"
                    name="amountToReceive"
                    className="!border-0 font-semibold !px-0 text-xl min-w-36"
                    value={value || "0"}
                    placeholder="0.00"
                    allowDecimals
                    maxLength={MAX_CHARACTER_AMOUNT}
                    onValueChange={(v) => {
                      isAmountToReceive.current = true;
                      onChange(v);
                    }}
                  />
                )}
              />
            )}
            <div className="h-full flex w-28 justify-start gap-1 flex-shrink-0 p-1 items-center overflow-visible">
              <Controller
                name="country"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    containerClassName="p-0 !w-24"
                    value={value}
                    className="font-semibold text-gray-800 !border-0 gap-1 !px-1"
                    options={countryOptions}
                    loading={loadingCountries}
                    onChange={(event) => {
                      handleCountryChange(event);
                      onChange(event.value);

                      const newCurrency = countries.find(
                        (country) => country.countryCode === event.value
                      )?.defaultCurrency;
                      if (newCurrency) {
                        setValue("currency", newCurrency);
                      }
                    }}
                    alignMenuRight
                    block
                  />
                )}
              />
            </div>
          </div>

          {error && (
            <div className="w-full">
              <p className="text-xs font-medium text-red-500">
                {tErrors("Quoter.QuoteError")}
              </p>
            </div>
          )}
        </div>

        <div className="w-full flex flex-col gap-1 mt-1">
          <p className="w-full text-left text-sm font-medium text-[#656972]">
            {t("Quoter.DeliveryMethod.Title")}
          </p>
          <Controller
            name="deliveryMethod"
            control={control}
            render={({ field: { onChange } }) => (
              <Selector
                options={deliveryMethodOptions}
                onChange={(value: string) => {
                  setDeliveryMethod(value as DeliveryMethodCode);
                  onChange(value as DeliveryMethodCode);
                }}
              />
            )}
          />
          <ReactSelect
            name="destination"
            isSearchable={false}
            maxMenuHeight={210}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            classNames={primarySelectClassNames as any}
            placeholder={
              deliveryMethod === DeliveryMethods.CASH_PICKUP
                ? t("Quoter.CPUPlaceholder")
                : t("Quoter.D2BPlaceholder")
            }
            value={
              (
                deliveryMethod === DeliveryMethods.CASH_PICKUP
                  ? CPUOptions?.length === 0
                  : D2BOptions?.length === 0
              )
                ? null
                : ({
                    label: selectedDestination?.destination.destination || "",
                    value: selectedDestination?.value || "",
                    destination: selectedDestination?.destination,
                  } as DestinationOption)
            }
            onChange={(newValue) => {
              if (!newValue) return;
              setSelectedDestination(newValue);
              if (deliveryMethod === DeliveryMethods.CASH_PICKUP) {
                setValue(
                  "cpuSelected",
                  newValue?.destination?.payerQuote || ""
                );
              } else {
                setValue(
                  "d2bSelected",
                  newValue?.destination?.payerQuote || ""
                );
              }
            }}
            isClearable={false}
            options={
              deliveryMethod === DeliveryMethods.CASH_PICKUP
                ? CPUOptions
                : D2BOptions
            }
            isLoading={loadingDestinations}
            isDisabled={
              deliveryMethod === DeliveryMethods.CASH_PICKUP
                ? CPUOptions?.length === 0
                : D2BOptions?.length === 0
            }
          />
          {deliveryMethod === DeliveryMethods.CASH_PICKUP &&
            CPUOptions?.length === 0 &&
            !loadingDestinations && (
              <p className="text-xs text-stone-400 text-center">
                {t("Quoter.NoCPUDestination")}
              </p>
            )}
          {deliveryMethod === DeliveryMethods.ACCOUNT_DEPOSIT &&
            D2BOptions?.length === 0 &&
            !loadingDestinations && (
              <p className="text-xs text-stone-400 text-center">
                {t("Quoter.NoD2BDestination")}
              </p>
            )}
        </div>

        <div className="w-full flex flex-col gap-1 mt-1">
          <p className="w-full text-left text-sm font-medium text-[#656972]">
            {t("Quoter.Channel.Title")}
          </p>
          <Controller
            name="channel"
            control={control}
            render={({ field: { onChange, value } }) => (
              <MultiSelector
                onChange={(option) => {
                  onChange(option.value);
                }}
                defaultOption={QuoteChannel.bestFx}
                value={value}
                options={[
                  {
                    content: (
                      <ChannelSelectCard
                        type={QuoteChannel.lessFee}
                        currency={selectedCountry.defaultCurrency}
                        fx={
                          quotesPerChannel?.[QuoteChannel.lessFee]
                            ?.deliveryMethods?.[0]?.fx || 0
                        }
                        transferFee={
                          quotesPerChannel?.[QuoteChannel.lessFee]
                            ?.deliveryMethods?.[0]?.fee || 0
                        }
                        available={Boolean(
                          quotesPerChannel?.[QuoteChannel.lessFee]?.amount
                        )}
                      />
                    ),
                    disabled: !quotesPerChannel?.[QuoteChannel.lessFee]?.amount,
                    value: QuoteChannel.lessFee,
                  },
                  {
                    content: (
                      <ChannelSelectCard
                        type={QuoteChannel.bestFx}
                        currency={selectedCountry.defaultCurrency}
                        fx={
                          quotesPerChannel?.[QuoteChannel.bestFx]
                            ?.deliveryMethods?.[0]?.fx || 0
                        }
                        transferFee={
                          quotesPerChannel?.[QuoteChannel.bestFx]
                            ?.deliveryMethods?.[0]?.fee || 0
                        }
                        available={Boolean(
                          quotesPerChannel?.[QuoteChannel.bestFx]?.amount
                        )}
                      />
                    ),
                    disabled: !quotesPerChannel?.[QuoteChannel.bestFx]?.amount,
                    value: QuoteChannel.bestFx,
                  },
                ]}
              />
            )}
          />
        </div>

        {!!paymentMethodOptions?.length && paymentMethodOptions.length > 0 && (
          <>
            <div className="w-full flex lg:hidden flex-col">
              <p className="w-full text-left text-sm font-medium text-[#656972]">
                {t("Quoter.PaymentMethod.Title")}
              </p>
              <ReactSelect
                name="paymentMethod"
                isSearchable={false}
                maxMenuHeight={230}
                onChange={(change) => handlePaymentMethodChange(change!.value)}
                isClearable={false}
                options={paymentMethodOptions}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                classNames={primarySelectClassNames as any}
                value={{
                  label: paymentMethod?.paymentOrigin || "",
                  value: paymentMethod?.paymentOrigin || "",
                  tariff: paymentMethodOptions.find(
                    (method) => method.label === paymentMethod?.paymentOrigin
                  )?.tariff,
                }}
                formatOptionLabel={({ label, tariff }) => {
                  return (
                    <PaymentMethodOption
                      label={label}
                      fee={formatNumber(tariff || 0)}
                      icon={
                        paymentMethodsMap?.[label as PaymentMethodCode]?.icon
                      }
                    />
                  );
                }}
                isLoading={loadingQuote || loadingDestinations}
              />
            </div>
            <div className="w-full hidden lg:flex flex-col">
              <p className="w-full text-left text-sm font-medium text-[#656972]">
                {t("Quoter.PaymentMethod.Title")}
              </p>
              <MultiSelector
                onChange={(option) => {
                  handlePaymentMethodChange(option.value);
                }}
                value={paymentMethod?.paymentOrigin}
                options={paymentMethodOptions.map((paymentMethod) => ({
                  content: (
                    <PaymentMethodSelectCard
                      icon={
                        paymentMethodsMap?.[
                          paymentMethod?.label as PaymentMethodCode
                        ]?.icon
                      }
                      label={paymentMethod.label}
                      tariff={formatNumber(paymentMethod.tariff)}
                    />
                  ),
                  value: paymentMethod.value,
                }))}
              />
            </div>
          </>
        )}

        <QuoteTotals
          amount={quotesPerChannel?.[channel]?.amount || 0}
          paymentMethodFee={
            (quotesPerChannel && paymentMethod?.paymentMethodFee) || 0
          }
          transferFee={
            quotesPerChannel?.[channel]?.deliveryMethods?.[0]?.fee || 0
          }
          total={(quotesPerChannel?.[channel] && paymentMethod?.totalCost) || 0}
        />

        <div className="inline-flex justify-evenly w-full gap-4 lg:gap-6">
          <Button
            className="!max-w-56 text-sm sm:text-base !bg-primary-500 !border-0 font-normal py-3 px-8 my-2"
            color="purple"
            text={t("Home.LoginButton")}
            link={`${AUTH_DOMAIN}/sign-in${addQueryParamsToSignIn(getValues())}`}
            rel={["noreferrer"]}
            block
          />

          <Button
            className="!max-w-56 text-sm sm:text-base !border-primary-500 !text-primary-500 font-normal py-3 px-8 my-2"
            color="purple"
            text={t("Home.SignupButton")}
            variant="secondary"
            link={`${AUTH_DOMAIN}/sign-up`}
            rel={["noreferrer"]}
            block
          />
        </div>
        <div className="w-full flex gap-2 items-center text-stone-500 max-w-lg">
          <InformationCircleIcon className="w-6 h-6 flex-shrink-0" />
          <p className="w-full leading-none">
            <span
              className="text-xs inner-span-bolden leading-tight"
              dangerouslySetInnerHTML={{
                __html: t("Quoter.Disclaimer4"),
              }}
            />{" "}
            <span
              onClick={showLegalInfo}
              className="text-xs leading-tight underline font-medium cursor-pointer"
            >
              {t("Quoter.DisclaimerViewMore")}
            </span>
          </p>
        </div>
      </form>
    </Card>
  );
};
